import { Card, CardContent, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Network, Trash } from "lucide-react";
import { CircuitTaggingType, L2VPNCircuit, L2VPNCircuitMapping } from "@/shared/types/l2vpnTypes";
import { Badge } from "@/shared/ui/badge";

interface L2VPNCircuitCardProps {
  l2vpnCircuit: L2VPNCircuit;
  onRemoveL2VPNCircuitRecordFromOrganization?: (recordId: string) => void;
}

const L2VPNCircuitCard = ({
  l2vpnCircuit,
  onRemoveL2VPNCircuitRecordFromOrganization,
}: L2VPNCircuitCardProps) => {
  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-4">
        {/* Header Section */}
        <div className="flex flex-col w-full">
          <div className="flex flex-row justify-between w-full mb-4">
            <div className="flex items-center space-x-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg border">
                <Network className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-lg">{l2vpnCircuit.circuitId}</CardTitle>
                <div className="flex flex-row items-center space-x-2">
                  <Badge variant="default" className="border">POP Name: {l2vpnCircuit.popName}</Badge>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-start space-y-2">
            <span className="text-sm font-medium">Associated L2VPN ranges for this circuit</span>
            {l2vpnCircuit.records?.map(record => (
              <div key={record.id} className="border border-border flex flex-col items-start space-y-2 rounded-lg p-4 w-full">
                <div className="flex flex-row items-center justify-between w-full">
                  <div className="flex flex-row items-center space-x-2">
                    <Network className="h-4 w-4" />
                    <div className="flex flex-col items-start space-y-1">
                      <p className="text-sm font-medium">Circuit Name: {record.circuitName}</p>
                      <div className="flex flex-row items-center space-x-2">
                        <Badge variant="default" className="border">Customer VLANs: {record.allocatedCustomerVlanRange}</Badge>
                        <Badge variant="default" className="border">Service VLANs: {record.allocatedServiceVlanRange}</Badge>
                      </div>
                    </div>
                  </div>
                  {onRemoveL2VPNCircuitRecordFromOrganization && ((record.mappings?.length ?? 0) === 0) && (
                    <div className="flex items-center justify-end">
                      <Button variant="destructive" size="sm" className="gap-2" onClick={() => onRemoveL2VPNCircuitRecordFromOrganization?.(record.id)}>
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-start space-y-2 w-full">
                  {record.mappings?.length > 0 ? (
                    <>
                      {Object.entries(
                        record.mappings.reduce((acc, mapping) => {
                          acc[mapping.taggingType] = acc[mapping.taggingType] || [];
                          acc[mapping.taggingType].push(mapping);
                          return acc;
                        }, {} as Record<CircuitTaggingType, L2VPNCircuitMapping[]>)
                      ).map(([taggingType, mappings]) => (
                        <div key={taggingType} className="flex flex-col items-start space-y-2 w-full">
                          <div className="flex flex-row items-center gap-2 text-sm font-medium">
                            Tagging Type:
                            <Badge variant="default" className="border">
                              {taggingType === CircuitTaggingType.QINQ ? 'Q-in-Q' : 'Single'}
                            </Badge>
                          </div>
                          {mappings.map((mapping, index) => (
                            <div key={`${index}`} className="border border-border flex flex-row items-center justify-between rounded-lg p-4 w-full">
                              <div className="flex flex-row items-center gap-2 text-sm font-medium">
                                <span>Terminal ID:</span>
                                <Badge variant="default" className="border">{mapping.userTerminalId}</Badge>
                              </div>
                              <div className="flex flex-row items-center gap-2 text-sm font-medium">
                                <span>Customer VLANs:</span>
                                <Badge variant="default" className="border">{mapping.customerVlans.join(', ')}</Badge>
                              </div>
                              <div className="flex flex-row items-center gap-2 text-sm font-medium">
                                <span>Service VLAN:</span>
                                <Badge variant="default" className="border">{mapping.serviceVlan}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </>
                  ) : (
                    <span className="text-sm font-medium italic text-red-500">No ranges are used from this circuit in a terminal yet.</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default L2VPNCircuitCard; 